<template>
  <div v-if="reasonForInvalid" class="video-instructions-invalid">
    <div class="video-instructions-invalid__icon">
      <div class="video-instructions-invalid__icon-outline-outer"></div>
      <div class="video-instructions-invalid__icon-outline-inner"></div>
      <div class="video-instructions-invalid__icon-inner">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M10 6.66667V10M10 13.3333H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z" 
            stroke="#D92D20" 
            stroke-width="1.66667" 
            stroke-linecap="round" 
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
    <div class="video-instructions-invalid__content">
      <div class="video-instructions-invalid__header">
        <h3 class="video-instructions-invalid__title">{{ invalidTitle }}</h3>
      </div>
      <div class="video-instructions-invalid__description">
        <p 
          v-for="(paragraph, index) in reasonParagraphs" 
          :key="index"
          class="video-instructions-invalid__text"
        >{{ paragraph }}</p>
      </div>
    </div>
  </div>
  <div v-else class="video-instructions-summary">
    <div class="video-instructions-summary__icon">
      <div class="video-instructions-summary__icon-inner">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M10 13.3333V10M10 6.66667H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z" 
            stroke="#344054" 
            stroke-width="1.66667" 
            stroke-linecap="round" 
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
    <div class="video-instructions-summary__content">
      <div class="video-instructions-summary__header">
        <h3 class="video-instructions-summary__title">{{ title }}</h3>
      </div>
      <div class="video-instructions-summary__items">
        <div 
          v-for="(item, index) in displayItems" 
          :key="index" 
          class="video-instructions-summary__item"
        >
          <p class="video-instructions-summary__text">
            <span class="video-instructions-summary__label">{{ item.label }}:</span>
            <span class="video-instructions-summary__description"> {{ item.description }}</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Summary'
  },
  rubric: {
    type: Object,
    default: () => ({}),
    validator: (value) => {
      return typeof value === 'object'
    }
  },
  reasonForInvalid: {
    type: String,
    default: null
  },
  invalidTitle: {
    type: String,
    default: 'There is a critical mismatch between the provided business context and the visual content of the video.'
  }
})

// Label display names mapping
const labelMap = {
  pass: 'Pass',
  borderline: 'Borderline',
  fail: 'Fail'
}

// Convert rubric object to display items array
const displayItems = computed(() => {
  if (!props.rubric || Object.keys(props.rubric).length === 0) {
    return []
  }
  
  // Define the order of keys
  const keyOrder = ['pass', 'borderline', 'fail']
  
  return keyOrder
    .filter(key => props.rubric[key])
    .map(key => ({
      label: labelMap[key] || capitalizeFirst(key),
      description: props.rubric[key]
    }))
})

// Split reason_for_invalid into paragraphs
const reasonParagraphs = computed(() => {
  if (!props.reasonForInvalid) {
    return []
  }
  return props.reasonForInvalid.split('\n\n').filter(p => p.trim())
})

const capitalizeFirst = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}
</script>

<style scoped>
.video-instructions-invalid {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #D0D5DD;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  background-color: #FFFFFF;
  width: 100%;
  box-sizing: border-box;
}

.video-instructions-invalid__icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-instructions-invalid__icon-outline-outer {
  position: absolute;
  inset: -45%;
  border-radius: 50%;
  border: 2px solid #D92D20;
  opacity: 0.1;
}

.video-instructions-invalid__icon-outline-inner {
  position: absolute;
  inset: -20%;
  border-radius: 50%;
  border: 2px solid #D92D20;
  opacity: 0.3;
}

.video-instructions-invalid__icon-inner {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.video-instructions-invalid__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.video-instructions-invalid__header {
  padding-right: 32px;
}

.video-instructions-invalid__title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #000000;
  margin: 0;
}

.video-instructions-invalid__description {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.video-instructions-invalid__text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  margin: 0;
}

.video-instructions-summary {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #D0D5DD;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  background-color: #FFFFFF;
  width: 100%;
  box-sizing: border-box;
  transition: background-color 0.2s ease;
}

.video-instructions-summary:hover {
  background-color: #F4EBFF;
  cursor: url("../assets/cursorArrow.svg"), auto;
}

.video-instructions-summary:hover .video-instructions-summary__icon {
  background-color: #F4EBFF;
}

.video-instructions-summary__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #EAECF0;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #FFFFFF;
  transition: background-color 0.2s ease;
}

.video-instructions-summary__icon-inner {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-instructions-summary__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.video-instructions-summary__header {
  padding-right: 32px;
}

.video-instructions-summary__title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
  margin: 0;
}

.video-instructions-summary__items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.video-instructions-summary__item {
  width: 100%;
}

.video-instructions-summary__text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
  margin: 0;
}

.video-instructions-summary__label {
  font-weight: 700;
}

.video-instructions-summary__description {
  font-weight: 400;
}
</style>

